import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";

import { tierHasCapability, tierDisplayName } from "@/lib/tier";
import {
  FAL_MODEL_OPTIONS,
  getImageProviderSettings,
  upsertImageProviderSettings,
} from "@/lib/imageProvider";
import type { Workspace } from "@/hooks/useWorkspace";

type Props = { workspace: Workspace };

/**
 * BYOK image-provider settings for the upcoming Post Image generator (Beta).
 * Currently supports Fal.ai. Keys are encrypted with the per-workspace key
 * system; only a 4-character preview ("••••abcd") is ever exposed to the UI.
 * The generator that consumes this key ships in a later release — the card
 * is intentionally kept alive so users can stage their credential now.
 */
export const ImageProviderSettingsCard = ({ workspace }: Props) => {
  const qc = useQueryClient();
  const allowed = tierHasCapability(workspace.account_tier ?? null, "content_image_provider_byok");

  const settingsQuery = useQuery({
    queryKey: ["image-provider", workspace.id],
    queryFn: () => getImageProviderSettings(workspace.id, "fal"),
    enabled: allowed,
  });

  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("fal-ai/flux/schnell");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settingsQuery.data) {
      setModel(settingsQuery.data.default_model_key ?? "fal-ai/flux/schnell");
      setEnabled(settingsQuery.data.enabled);
    }
  }, [settingsQuery.data]);

  if (!allowed) {
    return (
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Post image provider (Fal.ai) <Badge variant="secondary">Beta</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          The upcoming Post Image generator requires a Pro plan or higher. Your current plan is{" "}
          {tierDisplayName(workspace.account_tier ?? null)}.
        </CardContent>
      </Card>
    );
  }

  const existing = settingsQuery.data;
  const hasKey = !!existing?.api_key_preview;

  const save = async () => {
    setSaving(true);
    try {
      await upsertImageProviderSettings({
        workspaceId: workspace.id,
        provider: "fal",
        apiKey: apiKey.trim() || null, // empty = leave key untouched
        defaultModelKey: model,
        enabled,
      });
      toast.success("Image provider settings saved");
      setApiKey("");
      qc.invalidateQueries({ queryKey: ["image-provider", workspace.id] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    setSaving(true);
    try {
      // The RPC keeps the existing key when given null; to actually clear we
      // delete the row directly (one row per workspace).
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase
        .from("workspace_image_provider_settings")
        .delete()
        .eq("workspace_id", workspace.id);
      if (error) throw error;
      toast.success("API key removed");
      qc.invalidateQueries({ queryKey: ["image-provider", workspace.id] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Post image provider (Fal.ai) <Badge variant="secondary">Beta</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Reserved for the upcoming Post Image generator. You can store your Fal.ai key now; the
          generator that consumes it lands in a later release. Your key is encrypted at rest and
          only a short preview is ever shown back to you.{" "}
          <a
            href="https://fal.ai/dashboard/keys"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            Get a Fal.ai key <ExternalLink className="h-3 w-3" />
          </a>
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="fal-key">Fal.ai API key</Label>
          <Input
            id="fal-key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={hasKey ? `Current: ${existing!.api_key_preview}` : "fal_xxxxxxxxxxxx"}
            autoComplete="off"
          />
          <p className="text-xs text-muted-foreground">
            {hasKey
              ? "Leave blank to keep the existing key. Enter a new value to replace it."
              : "Paste your key, then save. It will be encrypted before it leaves the form."}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="fal-model">Default model</Label>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger id="fal-model"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FAL_MODEL_OPTIONS.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}{m.hint ? ` — ${m.hint}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Will be used as the default model once the Post Image generator ships.
          </p>
        </div>

        <div className="flex items-center justify-between rounded-md border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="fal-enabled">Enable image generation</Label>
            <p className="text-xs text-muted-foreground">
              Leave on to have the Post Image generator pick this workspace up automatically when it ships.
            </p>
          </div>
          <Switch id="fal-enabled" checked={enabled} onCheckedChange={setEnabled} />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save settings"}
          </Button>
          {hasKey && (
            <Button variant="outline" onClick={clearKey} disabled={saving}>
              Remove API key
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
